import { useEffect, useRef, useState } from 'react';
import type { FormProps } from 'antd';

export interface ErrorSummaryItem {
  fieldId: string;
  message: string;
  /** Własne przejście do pola (np. po rozwinięciu sekcji, w której leży) zamiast zwykłego fokusu. */
  onSelect?: () => void;
}

type FinishFailed<Values> = NonNullable<FormProps<Values>['onFinishFailed']>;
type FieldsChange<Values> = NonNullable<FormProps<Values>['onFieldsChange']>;
type FieldList<Values> = Parameters<FieldsChange<Values>>[1];

/**
 * Podsumowanie błędów nad formularzem: po nieudanym zapisie zbiera komunikaty pól, przenosi na nie
 * fokus, a w trakcie poprawiania odświeża listę (poprawione pole znika, po ostatnim znika całość).
 * Id pola antd to `<nazwa formularza>_<nazwa pola>`.
 */
export function useFormErrorSummary<Values>(formName: string) {
  const [items, setItems] = useState<ErrorSummaryItem[]>([]);
  const [focusRequest, setFocusRequest] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusRequest > 0) summaryRef.current?.focus();
  }, [focusRequest]);

  const toItem = (name: unknown, message: string): ErrorSummaryItem => ({
    fieldId: `${formName}_${([] as unknown[]).concat(name).join('_')}`,
    message,
  });

  const onFinishFailed: FinishFailed<Values> = ({ errorFields }) => {
    setItems(errorFields.map((field) => toItem(field.name, field.errors[0])));
    setFocusRequest((value) => value + 1);
  };

  const onFieldsChange: FieldsChange<Values> = (_changed, allFields: FieldList<Values>) => {
    setItems((current) =>
      current.length === 0
        ? current
        : allFields
            .filter((field) => (field.errors?.length ?? 0) > 0)
            .map((field) => toItem(field.name, (field.errors ?? [])[0]))
    );
  };

  return { items, summaryRef, onFinishFailed, onFieldsChange };
}
